import theme, { ThemeVariants } from '@theme';
import React, { useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import styled from 'styled-components';

import { Action, ActionInformation } from './types';
import ActionButton from './ActionButton';
import { Icon } from '../icon';
import { ActionState } from './constants';
import { LoaderIcon } from 'react-hot-toast';

interface DisplayActionProps {
  actions?: Action[];
}

interface CustomToggleProps {
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  actionInformation: ActionInformation;
}

interface CustomMenuProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  'aria-labelledby'?: string;
}

const CustomToggle = React.forwardRef<HTMLButtonElement, CustomToggleProps>(
  ({ onClick, actionInformation }, ref) => {
    switch (actionInformation.state) {
      case ActionState.IN_PROGRESS:
        return (
          <OfferActionButton
            ref={ref}
            onClick={(e) => {
              e.preventDefault();
              onClick(e);
            }}
            color="blue"
            disabled
          >
            <CustomLoader />
          </OfferActionButton>
        );

      case ActionState.COMPLETED:
        return (
          <OfferActionButton
            ref={ref}
            onClick={(e) => {
              e.preventDefault();
              onClick(e);
            }}
            color="grey"
            disabled
          >
            <CustomIcon iconName={actionInformation.iconName ?? 'vertical-dots'} />
          </OfferActionButton>
        );
      case ActionState.FAILED:
        return (
          <OfferActionButton
            ref={ref}
            onClick={(e) => {
              e.preventDefault();
              onClick(e);
            }}
            color="grey"
          >
            <CustomIcon iconName={'x-mark'} />
          </OfferActionButton>
        );
      default:
        return (
          <OfferActionButton
            ref={ref}
            onClick={(e) => {
              e.preventDefault();
              onClick(e);
            }}
            color="blue"
          >
            <CustomIcon iconName={'vertical-dots'} />
          </OfferActionButton>
        );
    }
  }
);
CustomToggle.displayName = 'CustomToggle';

const CustomMenu = React.forwardRef<HTMLDivElement, CustomMenuProps>(
  ({ children, style, className, 'aria-labelledby': labeledBy }, ref) => (
    <CustomMenuContainer ref={ref} style={style} className={className} aria-labelledby={labeledBy}>
      {children}
    </CustomMenuContainer>
  )
);
CustomMenu.displayName = 'CustomMenu';

const DisplayActions: React.FC<DisplayActionProps> = ({ actions }) => {
  const [actionInformation, setActionInformation] = useState<ActionInformation>({
    state: ActionState.NO_STATE
  });
  if (actions) {
    return (
      <Dropdown>
        <Dropdown.Toggle
          as={CustomToggle}
          id="dropdown-custom-components"
          actionInformation={actionInformation}
        />
        <Dropdown.Menu as={CustomMenu}>
          {actions.map((action) => (
            <ActionButton
              key={action.label}
              action={action}
              setActionInformation={setActionInformation}
            />
          ))}
        </Dropdown.Menu>
      </Dropdown>
    );
  }
  return <></>;
};

const CustomMenuContainer = styled.div`
  width: fit-content;
  height: fit-content;
  padding: 0.25rem;
  background-color: white;
`;

const CustomIcon = styled(Icon)`
  width: 0.688rem;
  height: 0.688rem;
`;

const CustomLoader = styled(LoaderIcon)`
  width: 0.688rem;
  height: 0.688rem;
`;

const OfferActionButton = styled.button<{ color: ThemeVariants }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 1.438rem;
  height: 1.5rem;
  border-width: 0px;
  border-radius: 0.25rem;
  color: ${({ color }) => theme.palette[color][500]};
  background-color: ${({ color }) => theme.palette[color][100]};
  &:hover {
    background-color: ${({ color }) => theme.palette[color][200]};
  }
`;

export default DisplayActions;
